import { useCallback, useRef, useState } from 'react';

import {
  useExamesControllerEnviarAnexo,
  useExamesControllerExtrairExterno,
} from '@/api/generated/exames/exames';
import type { ExtracaoDeExameResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';

/**
 * Em que pé está cada documento.
 *
 *  enviando    → subindo para o servidor;
 *  lendo       → já está lá (tem `arquivoId`), a IA está lendo;
 *  lido        → a leitura voltou e foi entregue em `aoLer`;
 *  sem-leitura → está anexado, mas não foi lido (ver `aviso` para o porquê).
 */
export type EstadoDoAnexo = 'enviando' | 'lendo' | 'lido' | 'sem-leitura';

export interface Anexo {
  /** `nome:tamanho` — `File` não tem identidade estável entre duas escolhas. */
  chave: string;
  arquivo: File;
  estado: EstadoDoAnexo;
  /** Presente a partir de `lendo`. É o que vai em `arquivoIds` ao salvar. */
  arquivoId?: string;
  /** Por que não foi lido, em linguagem de tela. Só em `sem-leitura`. */
  aviso?: string;
  /** Dá para tentar de novo — falha passageira, e não "não é um exame". */
  podeLerDeNovo?: boolean;
}

interface Opcoes {
  pacienteId: string;
  /** Chamado com cada leitura que voltou — quem consome decide como mesclar. */
  aoLer: (leitura: ExtracaoDeExameResponse) => void;
  /** O upload falhou: o documento já saiu da lista, e a tela avisa. */
  aoFalharOEnvio: (arquivo: File, mensagem: string) => void;
}

export const chaveDoArquivo = (arquivo: File): string => `${arquivo.name}:${arquivo.size}`;

/**
 * Documentos de um exame externo: sobem NA ESCOLHA e são lidos em seguida.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Antes o upload acontecia só no "Cadastrar". Agora ele precisa acontecer
 *  antes, porque a leitura automática trabalha sobre o arquivo já no servidor
 *  — e é ela que preenche o formulário que a pessoa vai conferir.
 *
 *  ⚠️ Três coisas que este hook garante, e que a tela não precisa repetir:
 *
 *  1. **Um de cada vez, em duas filas.** Uploads de até 10 MB numa, leituras
 *     de dezenas de segundos na outra: dez em paralelo competiriam pela mesma
 *     banda de subida e pelo mesmo limite de uso da IA. São filas separadas
 *     para o segundo documento subir enquanto o primeiro ainda é lido — o
 *     `arquivoId` é o que libera o "Cadastrar", a leitura não.
 *
 *  2. **Resposta atrasada não ressuscita.** Fechar o diálogo, remover o
 *     documento ou cancelar a leitura invalida o que ainda está no ar: quando a
 *     resposta chega, é descartada. Sem isso, a leitura do laudo do paciente
 *     anterior preencheria o formulário do seguinte.
 *
 *  3. **A leitura é opcional; o anexo, não.** IA fora do ar, documento
 *     ilegível, ambiente sem chave: o documento continua anexado e a pessoa
 *     preenche à mão. Nada aqui impede o cadastro.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function useAnexosComLeitura({ pacienteId, aoLer, aoFalharOEnvio }: Opcoes) {
  const { mutateAsync: enviar } = useExamesControllerEnviarAnexo();
  const { mutateAsync: extrair } = useExamesControllerExtrairExterno();

  const [anexos, setAnexos] = useState<Anexo[]>([]);
  /** O ambiente não tem IA: para de tentar, em vez de errar a cada documento. */
  const [leituraIndisponivel, setLeituraIndisponivel] = useState(false);

  // Refs, e não estado: são lidos de dentro de promessas que começaram vários
  // renders atrás, e o valor de um `useState` capturado lá estaria velho.
  const filaDeEnvio = useRef<Promise<void>>(Promise.resolve());
  const filaDeLeitura = useRef<Promise<void>>(Promise.resolve());
  /** Sobe a cada `reiniciar()`: o que começou numa geração não vale na seguinte. */
  const geracao = useRef(0);
  /** Os documentos que ESTÃO na lista. Sair daqui invalida envio e leitura. */
  const presentes = useRef(new Set<string>());
  /** A leitura vigente de cada documento. Cancelar ou ler de novo troca a senha. */
  const senhas = useRef(new Map<string, number>());
  const semIa = useRef(false);

  const aoLerAtual = useRef(aoLer);
  aoLerAtual.current = aoLer;
  const aoFalharAtual = useRef(aoFalharOEnvio);
  aoFalharAtual.current = aoFalharOEnvio;
  const pacienteAtual = useRef(pacienteId);
  pacienteAtual.current = pacienteId;

  const atualizar = useCallback((chave: string, mudanca: Partial<Anexo>) => {
    setAnexos((atuais) =>
      atuais.map((anexo) => (anexo.chave === chave ? { ...anexo, ...mudanca } : anexo)),
    );
  }, []);

  /** Enfileira a leitura de um documento que já está no servidor. */
  const enfileirarLeitura = useCallback(
    (chave: string, arquivoId: string) => {
      const daGeracao = geracao.current;
      const senha = (senhas.current.get(chave) ?? 0) + 1;
      senhas.current.set(chave, senha);
      const vigente = () =>
        geracao.current === daGeracao &&
        presentes.current.has(chave) &&
        senhas.current.get(chave) === senha;

      if (semIa.current) {
        atualizar(chave, { estado: 'sem-leitura', aviso: undefined, podeLerDeNovo: false });
        return;
      }
      atualizar(chave, { estado: 'lendo', aviso: undefined, podeLerDeNovo: false });

      filaDeLeitura.current = filaDeLeitura.current.then(async () => {
        if (!vigente()) return;
        if (semIa.current) {
          atualizar(chave, { estado: 'sem-leitura' });
          return;
        }

        try {
          const leitura = await extrair({ data: { arquivoId, pacienteId: pacienteAtual.current } });
          if (!vigente()) return;

          if (leitura.tipo === 'INDEFINIDO') {
            atualizar(chave, {
              estado: 'sem-leitura',
              aviso: 'Não parece um resultado de exame. Preencha os campos à mão.',
            });
            return;
          }

          aoLerAtual.current(leitura);
          atualizar(chave, { estado: 'lido' });
        } catch (erro) {
          if (!vigente()) return;

          if (erro instanceof ApiError && erro.codigo === 'IA_INDISPONIVEL') {
            semIa.current = true;
            setLeituraIndisponivel(true);
            atualizar(chave, { estado: 'sem-leitura' });
            return;
          }
          atualizar(chave, {
            estado: 'sem-leitura',
            aviso: 'Não foi possível ler este documento agora.',
            podeLerDeNovo: true,
          });
        }
      });
    },
    [atualizar, extrair],
  );

  const adicionar = useCallback(
    (arquivos: readonly File[]) => {
      const daGeracao = geracao.current;

      for (const arquivo of arquivos) {
        const chave = chaveDoArquivo(arquivo);
        // O mesmo laudo escolhido de novo: já está na lista, não sobe outra vez.
        if (presentes.current.has(chave)) continue;

        presentes.current.add(chave);
        setAnexos((atuais) => [...atuais, { chave, arquivo, estado: 'enviando' }]);

        filaDeEnvio.current = filaDeEnvio.current.then(async () => {
          const vigente = () => geracao.current === daGeracao && presentes.current.has(chave);
          if (!vigente()) return;

          try {
            const { arquivoId } = await enviar({ data: { arquivo } });
            if (!vigente()) return;

            atualizar(chave, { arquivoId });
            enfileirarLeitura(chave, arquivoId);
          } catch (erro) {
            if (!vigente()) return;

            presentes.current.delete(chave);
            setAnexos((atuais) => atuais.filter((anexo) => anexo.chave !== chave));
            aoFalharAtual.current(
              arquivo,
              erro instanceof ApiError
                ? erro.message
                : `Não foi possível enviar "${arquivo.name}".`,
            );
          }
        });
      }
    },
    [atualizar, enfileirarLeitura, enviar],
  );

  /** Tira o documento do exame. O que a leitura dele já preencheu continua na tela. */
  const remover = useCallback((chave: string) => {
    presentes.current.delete(chave);
    setAnexos((atuais) => atuais.filter((anexo) => anexo.chave !== chave));
  }, []);

  /** Desiste de esperar a leitura. O documento continua anexado. */
  const cancelarLeitura = useCallback(
    (chave: string) => {
      senhas.current.set(chave, (senhas.current.get(chave) ?? 0) + 1);
      atualizar(chave, { estado: 'sem-leitura', aviso: undefined, podeLerDeNovo: true });
    },
    [atualizar],
  );

  const lerDeNovo = useCallback(
    (chave: string) => {
      const arquivoId = anexos.find((anexo) => anexo.chave === chave)?.arquivoId;
      if (arquivoId) enfileirarLeitura(chave, arquivoId);
    },
    [anexos, enfileirarLeitura],
  );

  /** Diálogo reaberto: lista vazia, e tudo o que estava no ar perde a validade. */
  const reiniciar = useCallback(() => {
    geracao.current += 1;
    presentes.current.clear();
    senhas.current.clear();
    semIa.current = false;
    setLeituraIndisponivel(false);
    setAnexos([]);
  }, []);

  return {
    anexos,
    adicionar,
    remover,
    cancelarLeitura,
    lerDeNovo,
    reiniciar,
    leituraIndisponivel,
    /** Algum documento ainda está subindo — sem `arquivoId` não dá para salvar. */
    enviando: anexos.some((anexo) => anexo.estado === 'enviando'),
    lendo: anexos.some((anexo) => anexo.estado === 'lendo'),
  };
}
